import { useState, useEffect, useRef } from 'react';
import { SafeDatabaseUser } from '../types/types';

interface Use2FAPromptReturn {
  /**
   * Whether to show the 2FA setup prompt
   */
  showPrompt: boolean;

  /**
   * Handler for when the user chooses to continue without setting up 2FA
   */
  handleContinue: () => void;

  /**
   * Handler for when the user completes 2FA setup
   */
  handleSetupComplete: (updatedUser: SafeDatabaseUser) => void;
}

/**
 * Hook for managing the 2FA prompt UI state
 *
 * This hook handles the logic for when to display the 2FA setup prompt
 * to users who haven't yet enabled 2FA.
 */
const use2FAPrompt = (
  user: SafeDatabaseUser | null,
  setUser: (user: SafeDatabaseUser) => void,
): Use2FAPromptReturn => {
  const [showPrompt, setShowPrompt] = useState<boolean>(false);
  const [hasChecked, setHasChecked] = useState<boolean>(false);
  const previousEmailRef = useRef<string | undefined>(user?.email);

  useEffect(() => {
    if (user) {
      const previousEmail = previousEmailRef.current;
      const currentEmail = user.email;

      if ((!previousEmail || previousEmail === '') && currentEmail && currentEmail !== '') {
        setHasChecked(false);
      }

      previousEmailRef.current = currentEmail;
    }
  }, [user, user?.email]);

  // Check if the user needs to be prompted to set up 2FA
  useEffect(() => {
    if (user && !hasChecked) {
      // If the user is logged in and doesn't have 2FA enabled, show the prompt
      if (!user.twoFactorEnabled) {
        // Don't show immediately to avoid interrupting the login flow
        const timer = setTimeout(() => {
          if (user.email) {
            setShowPrompt(true);
          }
        }, 1000);

        return () => clearTimeout(timer);
      }

      setHasChecked(true);
    }

    return undefined;
  }, [user, hasChecked]);

  // Handle when the user chooses to continue without setting up 2FA
  const handleContinue = () => {
    setShowPrompt(false);
    setHasChecked(true);
  };

  // Handle when the user completes 2FA setup
  const handleSetupComplete = (updatedUser: SafeDatabaseUser) => {
    setUser(updatedUser);
    setShowPrompt(false);
    setHasChecked(true);
  };

  return {
    showPrompt,
    handleContinue,
    handleSetupComplete,
  };
};

export default use2FAPrompt;
